import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Lightbulb, ThumbsUp, MessageSquare, Filter, Plus,
  ArrowLeft, Clock, CheckCircle2, Loader2, Send,
  TrendingUp, Zap, Puzzle, Bug, HelpCircle, X, Settings, Bell, BellOff, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

const categoryIcons = {
  feature: Zap,
  improvement: TrendingUp,
  integration: Puzzle,
  bug_fix: Bug,
  other: HelpCircle
};

const categoryLabels = {
  feature: 'New Feature',
  improvement: 'Improvement',
  integration: 'Integration',
  bug_fix: 'Bug Fix',
  other: 'Other'
};

const categoryColors = {
  feature: 'bg-purple-100 text-purple-700 border-purple-200',
  improvement: 'bg-blue-100 text-blue-700 border-blue-200',
  integration: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  bug_fix: 'bg-red-100 text-red-700 border-red-200',
  other: 'bg-slate-100 text-slate-700 border-slate-200'
};

const statusColors = {
  pending: 'bg-slate-100 text-slate-600',
  under_review: 'bg-amber-100 text-amber-700',
  planned: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-600'
};

const statusLabels = {
  pending: 'Pending Review',
  under_review: 'Under Review',
  planned: 'Planned',
  in_progress: 'In Progress',
  completed: 'Completed',
  declined: 'Declined'
};

const IdeasPage = () => {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sortBy, setSortBy] = useState('votes');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [votingId, setVotingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Comments state
  const [expandedIdea, setExpandedIdea] = useState(null);
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(null);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);

  // Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [userSettings, setUserSettings] = useState({
    community_alias: '',
    disable_notifications: false
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const [newIdea, setNewIdea] = useState({
    title: '',
    description: '',
    category: 'feature',
    is_anonymous: false
  });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    fetchIdeas();
    fetchStats();
  }, [sortBy, filterCategory, filterStatus, currentUserId]);

  useEffect(() => {
    if (isLoggedIn && currentUserId) {
      fetchUserSettings();
    }
  }, [isLoggedIn, currentUserId]);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setIsLoggedIn(!!user);

    if (user) {
      // Get internal user ID and check admin status
      const { data: userData, error } = await supabase
        .from('users')
        .select('id, role')
        .eq('supabase_user_id', user.id)
        .single();

      if (!error && userData) {
        setCurrentUserId(userData.id);
        setIsAdmin(userData.role === 'superadmin' || userData.role === 'admin');
      }
    }
  };

  const handleStatusChange = async (ideaId, newStatus) => {
    try {
      const { error } = await supabase
        .from('ideas')
        .update({ status: newStatus })
        .eq('id', ideaId);

      if (error) throw error;

      setIdeas(ideas.map(idea =>
        idea.id === ideaId ? { ...idea, status: newStatus } : idea
      ));
      toast.success('Status updated');
      fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDeleteIdea = async (ideaId) => {
    if (!confirm('Are you sure you want to delete this idea? This action cannot be undone.')) {
      return;
    }

    setDeletingId(ideaId);
    try {
      // Delete all comments first
      await supabase
        .from('idea_comments')
        .delete()
        .eq('idea_id', ideaId);

      // Delete all upvotes
      await supabase
        .from('idea_upvotes')
        .delete()
        .eq('idea_id', ideaId);

      // Delete the idea
      const { error } = await supabase
        .from('ideas')
        .delete()
        .eq('id', ideaId);

      if (error) throw error;

      toast.success('Idea deleted successfully');
      setIdeas(ideas.filter(idea => idea.id !== ideaId));
    } catch (error) {
      console.error('Error deleting idea:', error);
      toast.error(error.message || 'Failed to delete idea');
    } finally {
      setDeletingId(null);
    }
  };

  const fetchUserSettings = async () => {
    if (!currentUserId) return;

    try {
      const { data, error } = await supabase
        .from('users')
        .select('community_alias, disable_idea_notifications')
        .eq('id', currentUserId)
        .single();

      if (!error && data) {
        setUserSettings({
          community_alias: data.community_alias || '',
          disable_notifications: data.disable_idea_notifications || false
        });
      }
    } catch (error) {
      console.error('Error fetching user settings:', error);
    }
  };

  const handleSaveSettings = async () => {
    if (!currentUserId) return;

    setSavingSettings(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          community_alias: userSettings.community_alias || null,
          disable_idea_notifications: userSettings.disable_notifications
        })
        .eq('id', currentUserId);

      if (error) throw error;

      toast.success('Settings saved!');
      setShowSettingsModal(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchIdeas = async () => {
    try {
      let query = supabase
        .from('ideas')
        .select(`
          *,
          users:user_id (
            name,
            email,
            community_alias
          )
        `);

      // Apply filters
      if (filterCategory) {
        query = query.eq('category', filterCategory);
      }
      if (filterStatus) {
        query = query.eq('status', filterStatus);
      }

      // Apply sorting
      if (sortBy === 'votes') {
        query = query.order('upvote_count', { ascending: false });
      } else if (sortBy === 'newest') {
        query = query.order('created_at', { ascending: false });
      } else if (sortBy === 'oldest') {
        query = query.order('created_at', { ascending: true });
      }

      const { data: ideasData, error } = await query;

      if (error) throw error;

      // Get user's upvotes if logged in
      let userUpvotes = [];
      if (currentUserId) {
        const { data: upvotes } = await supabase
          .from('idea_upvotes')
          .select('idea_id')
          .eq('user_id', currentUserId);

        userUpvotes = (upvotes || []).map(u => u.idea_id);
      }

      // Format ideas with user info
      const formattedIdeas = (ideasData || []).map(idea => ({
        ...idea,
        user_name: idea.is_anonymous ? null : (idea.users?.community_alias || idea.users?.name || idea.users?.email?.split('@')[0]),
        has_upvoted: userUpvotes.includes(idea.id)
      }));

      setIdeas(formattedIdeas);
    } catch (error) {
      console.error('Error fetching ideas:', error);
      toast.error('Failed to load ideas');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase
        .from('ideas')
        .select('status');

      if (error) throw error;

      const statusCounts = (data || []).reduce((acc, idea) => {
        acc[idea.status] = (acc[idea.status] || 0) + 1;
        return acc;
      }, {});

      setStats({
        total: data?.length || 0,
        by_status: statusCounts
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newIdea.title.trim() || !newIdea.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!currentUserId) {
      toast.error('Please log in to submit an idea');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('ideas')
        .insert({
          user_id: currentUserId,
          title: newIdea.title.trim(),
          description: newIdea.description.trim(),
          category: newIdea.category,
          is_anonymous: newIdea.is_anonymous,
          status: 'pending',
          upvote_count: 0
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Your idea has been submitted!');
      setShowSubmitModal(false);
      setNewIdea({ title: '', description: '', category: 'feature', is_anonymous: false });
      fetchIdeas();
      fetchStats();
    } catch (error) {
      console.error('Error submitting idea:', error);
      toast.error(error.message || 'Failed to submit idea');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (ideaId) => {
    if (!isLoggedIn || !currentUserId) {
      toast.error('Please log in to vote');
      return;
    }

    setVotingId(ideaId);
    try {
      // Check if user has already voted
      const { data: existingVote } = await supabase
        .from('idea_upvotes')
        .select('id')
        .eq('idea_id', ideaId)
        .eq('user_id', currentUserId)
        .single();

      if (existingVote) {
        // Remove vote
        await supabase
          .from('idea_upvotes')
          .delete()
          .eq('id', existingVote.id);

        // Decrement count
        const { error: updateError } = await supabase
          .rpc('decrement_idea_upvote', { idea_id: ideaId });

        // If RPC doesn't exist, do it manually
        if (updateError) {
          const idea = ideas.find(i => i.id === ideaId);
          await supabase
            .from('ideas')
            .update({ upvote_count: Math.max(0, (idea?.upvote_count || 1) - 1) })
            .eq('id', ideaId);
        }

        setIdeas(ideas.map(idea => {
          if (idea.id === ideaId) {
            return {
              ...idea,
              upvote_count: Math.max(0, idea.upvote_count - 1),
              has_upvoted: false
            };
          }
          return idea;
        }));

        toast.success('Vote removed');
      } else {
        // Add vote
        await supabase
          .from('idea_upvotes')
          .insert({
            idea_id: ideaId,
            user_id: currentUserId
          });

        // Increment count
        const { error: updateError } = await supabase
          .rpc('increment_idea_upvote', { idea_id: ideaId });

        // If RPC doesn't exist, do it manually
        if (updateError) {
          const idea = ideas.find(i => i.id === ideaId);
          await supabase
            .from('ideas')
            .update({ upvote_count: (idea?.upvote_count || 0) + 1 })
            .eq('id', ideaId);
        }

        setIdeas(ideas.map(idea => {
          if (idea.id === ideaId) {
            return {
              ...idea,
              upvote_count: idea.upvote_count + 1,
              has_upvoted: true
            };
          }
          return idea;
        }));

        toast.success('Vote added!');
      }
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to vote');
    } finally {
      setVotingId(null);
    }
  };

  // Comment functions
  const fetchComments = async (ideaId) => {
    setLoadingComments(ideaId);
    try {
      const { data, error } = await supabase
        .from('idea_comments')
        .select(`
          *,
          users:user_id (
            name,
            email
          )
        `)
        .eq('idea_id', ideaId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const formattedComments = (data || []).map(comment => ({
        ...comment,
        user_name: comment.users?.name,
        user_email: comment.users?.email,
        is_own_comment: comment.user_id === currentUserId
      }));

      setComments(prev => ({ ...prev, [ideaId]: formattedComments }));
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(null);
    }
  };

  const toggleComments = async (ideaId) => {
    if (expandedIdea === ideaId) {
      setExpandedIdea(null);
    } else {
      setExpandedIdea(ideaId);
      if (!comments[ideaId]) {
        await fetchComments(ideaId);
      }
    }
  };

  const handleAddComment = async (ideaId) => {
    if (!isLoggedIn || !currentUserId) {
      toast.error('Please log in to comment');
      return;
    }

    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setSubmittingComment(true);
    try {
      const { data, error } = await supabase
        .from('idea_comments')
        .insert({
          idea_id: ideaId,
          user_id: currentUserId,
          content: newComment.trim()
        })
        .select(`
          *,
          users:user_id (
            name,
            email
          )
        `)
        .single();

      if (error) throw error;

      const formattedComment = {
        ...data,
        user_name: data.users?.name,
        user_email: data.users?.email,
        is_own_comment: true
      };

      setComments(prev => ({
        ...prev,
        [ideaId]: [...(prev[ideaId] || []), formattedComment]
      }));
      setNewComment('');
      toast.success('Comment added!');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(error.message || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (ideaId, commentId) => {
    if (!confirm('Delete this comment?')) return;

    setDeletingCommentId(commentId);
    try {
      const { error } = await supabase
        .from('idea_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      setComments(prev => ({
        ...prev,
        [ideaId]: prev[ideaId].filter(c => c.id !== commentId)
      }));
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error(error.message || 'Failed to delete comment');
    } finally {
      setDeletingCommentId(null);
    }
  };

  return (
    <div className="min-h-[calc(100vh-56px)] bg-[#F8F9FA]">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Lightbulb className="w-7 h-7 mr-3 text-[#74A57F]" />
              Ideas Portal
            </h1>
            <p className="text-slate-600 mt-1">
              Share and vote on feature ideas
            </p>
          </div>

          <div className="flex items-center space-x-3">
            {isLoggedIn && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettingsModal(true)}
                className="text-slate-600 hover:text-[#003B5C]"
                data-testid="ideas-settings-btn"
              >
                <Settings className="w-4 h-4" />
              </Button>
            )}
            {isLoggedIn ? (
              <Button
                onClick={() => setShowSubmitModal(true)}
                className="bg-[#003B5C] hover:bg-[#002A42] text-white"
                data-testid="submit-idea-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Submit Idea
              </Button>
            ) : (
              <Link to="/login">
                <Button className="bg-[#003B5C] hover:bg-[#002A42] text-white">
                  Log in to Submit
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 pb-12">
        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
              <div className="text-2xl font-bold text-[#003B5C]">{stats.total}</div>
              <div className="text-xs text-slate-500">Total Ideas</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
              <div className="text-2xl font-bold text-amber-500">{stats.by_status?.under_review || 0}</div>
              <div className="text-xs text-slate-500">Under Review</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
              <div className="text-2xl font-bold text-purple-500">{stats.by_status?.in_progress || 0}</div>
              <div className="text-xs text-slate-500">In Progress</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
              <div className="text-2xl font-bold text-green-500">{stats.by_status?.completed || 0}</div>
              <div className="text-xs text-slate-500">Completed</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600 font-medium">Filters:</span>
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#74A57F]"
              data-testid="sort-select"
            >
              <option value="votes">Most Voted</option>
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#74A57F]"
              data-testid="category-filter"
            >
              <option value="">All Categories</option>
              <option value="feature">New Feature</option>
              <option value="improvement">Improvement</option>
              <option value="integration">Integration</option>
              <option value="bug_fix">Bug Fix</option>
              <option value="other">Other</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#74A57F]"
              data-testid="status-filter"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending Review</option>
              <option value="under_review">Under Review</option>
              <option value="planned">Planned</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Ideas List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
          </div>
        ) : ideas.length === 0 ? (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <Lightbulb className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">No ideas yet</h3>
            <p className="text-slate-500 mb-4">Be the first to share your idea!</p>
            {isLoggedIn && (
              <Button onClick={() => setShowSubmitModal(true)} className="bg-[#003B5C] text-white">
                <Plus className="w-4 h-4 mr-2" />
                Submit First Idea
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {ideas.map((idea, index) => {
              const CategoryIcon = categoryIcons[idea.category] || HelpCircle;
              return (
                <motion.div
                  key={idea.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow"
                  data-testid={`idea-card-${idea.id}`}
                >
                  <div className="flex gap-4">
                    {/* Vote Button */}
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => handleVote(idea.id)}
                        disabled={votingId === idea.id}
                        className={`flex flex-col items-center justify-center w-16 h-20 rounded-xl border-2 transition-all ${idea.has_upvoted
                          ? 'bg-[#74A57F] border-[#74A57F] text-white'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-[#74A57F] hover:text-[#74A57F]'
                          }`}
                        data-testid={`vote-btn-${idea.id}`}
                      >
                        {votingId === idea.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <ThumbsUp className={`w-5 h-5 mb-1 ${idea.has_upvoted ? 'fill-current' : ''}`} />
                            <span className="text-lg font-bold">{idea.upvote_count}</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${categoryColors[idea.category]}`}>
                          <CategoryIcon className="w-3 h-3 mr-1" />
                          {categoryLabels[idea.category]}
                        </span>
                        {isAdmin ? (
                          <select
                            value={idea.status}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleStatusChange(idea.id, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className={`px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#74A57F] ${statusColors[idea.status]}`}
                            data-testid={`status-select-${idea.id}`}
                          >
                            {Object.entries(statusLabels).map(([val, label]) => (
                              <option key={val} value={val} className="text-slate-800 bg-white font-normal">
                                {label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusColors[idea.status]}`}>
                            {statusLabels[idea.status]}
                          </span>
                        )}
                      </div>

                      <h3 className="text-lg font-semibold text-[#003B5C] mb-2">
                        {idea.title}
                      </h3>

                      <p className="text-slate-600 text-sm mb-3 line-clamp-3">
                        {idea.description}
                      </p>

                      {idea.admin_response && (
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 mb-3">
                          <div className="flex items-center space-x-2 mb-1">
                            <MessageSquare className="w-4 h-4 text-blue-600" />
                            <span className="text-xs font-medium text-blue-700">Team Response</span>
                          </div>
                          <p className="text-sm text-blue-800">{idea.admin_response}</p>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {new Date(idea.created_at).toLocaleDateString()}
                          </span>
                          {!idea.is_anonymous && idea.user_name && (
                            <span>by {idea.user_name}</span>
                          )}
                        </div>
                        {isAdmin && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteIdea(idea.id);
                            }}
                            disabled={deletingId === idea.id}
                            className="flex items-center text-red-500 hover:text-red-700 transition-colors"
                            title="Delete idea"
                            data-testid={`delete-idea-${idea.id}`}
                          >
                            {deletingId === idea.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Comments Toggle Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleComments(idea.id);
                        }}
                        className="mt-3 flex items-center space-x-2 text-sm text-slate-500 hover:text-[#003B5C] transition-colors"
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span>
                          {comments[idea.id]?.length || 0} comments
                          {expandedIdea === idea.id ? ' ▲' : ' ▼'}
                        </span>
                      </button>

                      {/* Comments Section */}
                      {expandedIdea === idea.id && (
                        <div className="mt-4 border-t border-slate-100 pt-4">
                          {loadingComments === idea.id ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              <span className="ml-2 text-sm text-slate-500">Loading comments...</span>
                            </div>
                          ) : (
                            <>
                              {/* Existing Comments */}
                              {comments[idea.id]?.length > 0 ? (
                                <div className="space-y-3 mb-4">
                                  {comments[idea.id].map((comment) => (
                                    <div key={comment.id} className="bg-slate-50 rounded-lg p-3">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center space-x-2">
                                            <span className="text-sm font-medium text-[#003B5C]">
                                              {comment.user_name || comment.user_email?.split('@')[0]}
                                            </span>
                                            <span className="text-xs text-slate-400">
                                              {new Date(comment.created_at).toLocaleString()}
                                            </span>
                                          </div>
                                          <p className="text-sm text-slate-600 mt-1">{comment.content}</p>
                                        </div>
                                        {(comment.is_own_comment || isAdmin) && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteComment(idea.id, comment.id);
                                            }}
                                            disabled={deletingCommentId === comment.id}
                                            className="text-red-400 hover:text-red-600 ml-2"
                                            title="Delete comment"
                                          >
                                            {deletingCommentId === comment.id ? (
                                              <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : (
                                              <Trash2 className="w-3 h-3" />
                                            )}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-slate-400 mb-4">No comments yet. Be the first to comment!</p>
                              )}

                              {/* Add Comment Form */}
                              {isLoggedIn ? (
                                <div className="flex space-x-2">
                                  <Input
                                    placeholder="Add a comment..."
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment(idea.id)}
                                    className="flex-1 text-sm"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddComment(idea.id);
                                    }}
                                    disabled={submittingComment || !newComment.trim()}
                                    size="sm"
                                    className="bg-[#74A57F] hover:bg-[#5F8575] text-white"
                                  >
                                    {submittingComment ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Send className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              ) : (
                                <p className="text-sm text-slate-400">
                                  <Link to="/login" className="text-[#003B5C] hover:underline">Log in</Link> to add a comment
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>

      {/* Submit Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-[#003B5C]">Submit Your Idea</h2>
                </div>
                <button
                  onClick={() => setShowSubmitModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <Input
                  value={newIdea.title}
                  onChange={(e) => setNewIdea({ ...newIdea, title: e.target.value })}
                  placeholder="Brief summary of your idea"
                  maxLength={200}
                  required
                  data-testid="idea-title-input"
                />
                <p className="text-xs text-slate-400 mt-1">{newIdea.title.length}/200 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={newIdea.category}
                  onChange={(e) => setNewIdea({ ...newIdea, category: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#74A57F]"
                  data-testid="idea-category-select"
                >
                  <option value="feature">New Feature</option>
                  <option value="improvement">Improvement</option>
                  <option value="integration">Integration</option>
                  <option value="bug_fix">Bug Fix</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={newIdea.description}
                  onChange={(e) => setNewIdea({ ...newIdea, description: e.target.value })}
                  placeholder="Describe your idea in detail. What problem does it solve? How would it work?"
                  rows={5}
                  required
                  data-testid="idea-description-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={newIdea.is_anonymous}
                  onChange={(e) => setNewIdea({ ...newIdea, is_anonymous: e.target.checked })}
                  className="rounded border-slate-300 text-[#74A57F] focus:ring-[#74A57F]"
                />
                <label htmlFor="anonymous" className="text-sm text-slate-600">
                  Submit anonymously (your name won't be shown)
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowSubmitModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#003B5C] hover:bg-[#002A42] text-white"
                  data-testid="submit-idea-confirm-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Idea
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full"
          >
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-slate-400 to-slate-600 rounded-xl flex items-center justify-center">
                    <Settings className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-[#003B5C]">Ideas Portal Settings</h2>
                </div>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Community Alias */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Community Alias
                </label>
                <Input
                  value={userSettings.community_alias}
                  onChange={(e) => setUserSettings({ ...userSettings, community_alias: e.target.value })}
                  placeholder="Your display name in Ideas Portal"
                  maxLength={100}
                  data-testid="community-alias-input"
                />
                <p className="text-xs text-slate-500 mt-1.5">
                  This name will be shown on your idea submissions instead of your email/name.
                  Leave blank to use your account name.
                </p>
              </div>

              {/* Notification Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-center space-x-3">
                  {userSettings.disable_notifications ? (
                    <BellOff className="w-5 h-5 text-slate-400" />
                  ) : (
                    <Bell className="w-5 h-5 text-[#74A57F]" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-slate-700">In-App Notifications</p>
                    <p className="text-xs text-slate-500">
                      {userSettings.disable_notifications
                        ? 'Notifications are currently disabled'
                        : 'Get notified about idea status updates'
                      }
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setUserSettings({
                    ...userSettings,
                    disable_notifications: !userSettings.disable_notifications
                  })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${userSettings.disable_notifications ? 'bg-slate-300' : 'bg-[#74A57F]'
                    }`}
                  data-testid="notifications-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${userSettings.disable_notifications ? 'translate-x-1' : 'translate-x-6'
                      }`}
                  />
                </button>
              </div>

              {/* Save Button */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={() => setShowSettingsModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="bg-[#003B5C] hover:bg-[#002A42] text-white"
                  data-testid="save-settings-btn"
                >
                  {savingSettings ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default IdeasPage;
